import React, { useState } from 'react';
import { Card, CardHeader, CardBody, Text, Flex, Box, TagGroup, Tag, ButtonLink, ButtonIcon } from '@backstage/ui';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import {
  RiRefreshLine,
  RiGitRepositoryLine,
  RiBookOpenLine,
  RiUserLine,
  RiPriceTag3Line,
  RiStackLine,
  RiGlobalLine,
} from '@remixicon/react';
import { MetadataItem } from './metadata-item.component';
import { TypeBadge } from '../badges/type-badge.component';
import { LifecycleBadge } from '../badges/lifecycle-badge.component';
import { useSourceLocation } from '../../hooks';
import styles from './about.module.css';

function parseRef(value: string, defaultKind: string) {
  const { kind, namespace, name } = parseEntityRef(value, { defaultKind, defaultNamespace: 'default' });
  return { kind: kind.toLowerCase(), namespace: namespace ?? 'default', name };
}

export const AboutCard = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const { t } = useTranslationRef(appTranslationRef);
  const [refreshing, setRefreshing] = useState(false);

  const description = entity.metadata.description;
  const tags = entity.metadata.tags || [];
  const owner = (entity.spec?.owner as string) || undefined;
  const type = (entity.spec?.type as string) || undefined;
  const lifecycle = (entity.spec?.lifecycle as string) || undefined;
  const system = (entity.spec?.system as string) || undefined;
  const domain = (entity.spec?.domain as string) || undefined;

  const { sourceUrl } = useSourceLocation();
  const kind = entity.kind.toLowerCase();
  const showDocs = kind === 'component' || kind === 'resource';
  const hasQuickActions = sourceUrl || showDocs;

  // Build metadata items dynamically based on available data
  const metadataItems: { icon: React.ReactNode; label: string; value: string | undefined; href?: string }[] = [];

  if (owner !== undefined) {
    const ownerRef = parseRef(owner, 'group');
    metadataItems.push({
      icon: <RiUserLine size={16} />,
      label: t('about.owner'),
      value: ownerRef.name,
      href: `/catalog/${ownerRef.namespace}/${ownerRef.kind}/${ownerRef.name}`,
    });
  }

  if (domain) {
    const domainRef = parseRef(domain, 'domain');
    metadataItems.push({
      icon: <RiGlobalLine size={16} />,
      label: t('about.domain'),
      value: domainRef.name,
      href: `/catalog/${domainRef.namespace}/${domainRef.kind}/${domainRef.name}`,
    });
  }

  if (system) {
    const systemRef = parseRef(system, 'system');
    metadataItems.push({
      icon: <RiStackLine size={16} />,
      label: t('about.system'),
      value: systemRef.name,
      href: `/catalog/${systemRef.namespace}/${systemRef.kind}/${systemRef.name}`,
    });
  }

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const entityRef = `${entity.kind}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
      await catalogApi.refreshEntity(entityRef);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh entity:', error);
    } finally {
      setTimeout(() => setRefreshing(false), 1000);
    }
  };

  return (
    <Card>
      <CardHeader>
        <Flex justify="between" align="center">
          <Flex gap="3" align="center">
            <Text variant="title-small" weight="bold">
              {t('about.title')}
            </Text>
            {(type || lifecycle) && (
              <Flex gap="2" align="center">
                {type && <TypeBadge type={type} />}
                {lifecycle && <LifecycleBadge lifecycle={lifecycle} />}
              </Flex>
            )}
          </Flex>
          <ButtonIcon
            icon={<RiRefreshLine size={18} />}
            onPress={handleRefresh}
            isDisabled={refreshing}
            variant="tertiary"
            size="small"
            aria-label={t('about.refresh')}
          />
        </Flex>
      </CardHeader>

      <CardBody>
        <Flex direction="column" gap="5">
          {description && (
            <Box>
              <Text variant="body-medium" className={styles.descriptionText}>
                {description}
              </Text>
            </Box>
          )}

          {hasQuickActions && (
            <Flex gap="3" className={styles.quickActions}>
              {sourceUrl && (
                <ButtonLink
                  href={sourceUrl}
                  target="_blank"
                  variant="secondary"
                  size="small"
                  iconStart={<RiGitRepositoryLine size={16} />}
                >
                  {t('about.viewSource')}
                </ButtonLink>
              )}
              {showDocs && (
                <ButtonLink
                  href={`/docs/${entity.metadata.namespace || 'default'}/${entity.kind}/${entity.metadata.name}`}
                  variant="secondary"
                  size="small"
                  iconStart={<RiBookOpenLine size={16} />}
                >
                  {t('about.viewDocs')}
                </ButtonLink>
              )}
            </Flex>
          )}

          {metadataItems.length > 0 && (
            <Box className={styles.metadataGrid}>
              {metadataItems.map(item => (
                <MetadataItem
                  key={item.label}
                  icon={item.icon}
                  label={item.label}
                  value={item.value}
                  href={item.href}
                />
              ))}
            </Box>
          )}

          {tags.length > 0 && (
            <Box>
              <Flex gap="2" align="center" className={styles.tagsHeader}>
                <RiPriceTag3Line size={14} className={styles.tagIcon} />
                <Text variant="body-x-small" color="secondary">
                  {t('about.tags')}
                </Text>
              </Flex>
              <TagGroup>
                {tags.map((tag) => (
                  <Tag size='medium' key={tag}>{tag}</Tag>
                ))}
              </TagGroup>
            </Box>
          )}
        </Flex>
      </CardBody>
    </Card>
  );
};
